/** Portfolio StrategySpec — the original format (docs/10-strategies-and-backtests.md §2). New strategies use the one
 *  format in unified.ts; these still parse (and compile exactly as before) through `fromLegacy`. */
import { z } from "zod";

export const Market = z.enum(["spot", "futures", "spot+futures"]);
export const Quote = z.enum(["USDT", "USDC", "FDUSD", "BTC"]);

export const Universe = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("manual"), symbols: z.array(z.string().min(2)).min(1).max(50) }),
  z.object({ mode: z.literal("topVolume"), k: z.number().int().min(5).max(30), pool: z.string(), lookbackDays: z.number().int().min(7).max(365).default(30) }),
]);

export const Signal = z.discriminatedUnion("type", [
  z.object({ type: z.literal("momentum"), days: z.number().int().min(3).max(90), weight: z.number().positive().default(1) }),
  z.object({ type: z.literal("calm"), weight: z.number().min(0).max(1) }),
  z.object({ type: z.literal("carry"), entryApr: z.number(), exitApr: z.number(), coins: z.array(z.string()).min(1) }),
  z.object({ type: z.literal("shorts"), weight: z.number().min(0).max(1) }),
]);

/** Market guard (reports/regime): scale the whole book by a slow market state. btcTrend: the share of BTC's daily
 *  moving averages the price is above (decided at the daily close); altBear: `mult` while the altcoin basket is down
 *  more than `below` over `days`. */
export const Guard = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("btcTrend"), smaDays: z.array(z.number().int().min(5).max(400)).min(1).max(12).default([20, 30, 40, 50, 60, 80, 100, 120, 150, 200]) }).strict(),
  z.object({ kind: z.literal("altBear"), days: z.number().int().min(3).max(180).default(30), below: z.number().min(-0.9).max(0).default(-0.12), mult: z.number().min(0).max(1).default(0.5) }).strict(),
]);
export type Guard = z.infer<typeof Guard>;

export const StrategySpec = z.object({
  kind: z.literal("portfolio").optional(),                       // absent on specs saved before rules strategies existed
  name: z.string().min(1).max(60),
  market: Market,
  venue: z.literal("binance").default("binance"),
  quote: Quote.default("USDT"),
  universe: Universe,
  signals: z.array(Signal).min(1),
  sizing: z.object({
    riskLevel: z.number().min(0.1).max(0.8),
    rebalanceHours: z.union([z.literal(1), z.literal(4), z.literal(8), z.literal(24)]).default(8),
    maxLeverage: z.number().min(1).max(10).default(10),
    netCap: z.number().min(0).max(1).default(0.15),
    boost: z.object({ gain: z.number().min(0), extra: z.number().min(0) }).optional(),
    netCapScope: z.enum(["all", "traded"]).optional(),         // absent = "all" (versions saved before 2026-10); "traded" = only coins traded at t
  }),
  guard: Guard.optional(),
  safety: z.object({
    throttle: z.array(z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)])).default([[0.05, 0.7], [0.1, 0.5], [0.15, 0.35]]),
    stopAt: z.number().min(0.1).max(0.5).default(0.3),
  }).default({}),
  collateral: z.enum(["quote", "BTC", "BTC-trend"]).default("quote"),   // BTC-trend: hold the starting BTC only while BTC trends up
  frozen: z.object({ fdm: z.number().positive(), feeSchedule: z.string(), engineVersion: z.string() }).optional(),
});
export type StrategySpec = z.infer<typeof StrategySpec>;
export type StrategySpecInput = z.input<typeof StrategySpec>;

export interface SpecIssue { level: "error" | "warning"; field: string; message: string }

/** Rules that don't need market data (symbol existence is checked by the server against the catalogue). */
export function validateSpec(spec: StrategySpec): SpecIssue[] {
  const out: SpecIssue[] = [];
  const has = (t: string) => spec.signals.some((s) => s.type === t);
  if (spec.market === "spot") {
    if (has("shorts")) out.push({ level: "error", field: "signals", message: "Spot can't bet down: remove 'Bet down on weak coins'." });
    if (has("carry")) out.push({ level: "error", field: "signals", message: "The carry trade needs futures: switch the market to Spot + futures." });
    if (spec.sizing.maxLeverage !== 1) out.push({ level: "error", field: "sizing.maxLeverage", message: "Spot can't borrow: leverage must be 1×." });
  }
  if (spec.quote === "BTC" && spec.market !== "spot") out.push({ level: "error", field: "quote", message: "BTC-quoted pairs exist on spot only." });
  if (!has("momentum") && !has("carry")) out.push({ level: "error", field: "signals", message: "Add at least one momentum or carry signal." });
  if (spec.universe.mode === "manual") {
    out.push({ level: "warning", field: "universe", message: "Hand-picked coins are the ones still alive today, so backtests look better than reality. 'Top by volume' avoids this." });
    if (spec.universe.symbols.length < 10) out.push({ level: "warning", field: "universe", message: "Fewer than 10 coins: each bet is bigger and results swing more." });
  }
  if (spec.sizing.rebalanceHours === 1) out.push({ level: "warning", field: "sizing.rebalanceHours", message: "Hourly rebalancing roughly triples fees versus every 8 hours." });
  if (spec.sizing.riskLevel > 0.5) out.push({ level: "warning", field: "sizing.riskLevel", message: "Risk above 50% means deeper drops; check the backtest's worst drop." });
  return out;
}

/** Fee per unit traded for a market (docs/10 §2.3). */
export function feeSchedule(market: StrategySpec["market"]): { id: string; momentum: number } {
  return market === "spot" ? { id: "binance-spot-taker-0.10", momentum: 0.001 } : { id: "binance-futures-maker-first-0.035", momentum: 0.00035 };
}

